/**
 * DealWizard — slim orchestrator (Phase 27 refactor).
 *
 * Was a 1709-line monolith. Now ~170 lines. All state, effects, autofill,
 * regulation lookups, validation, payload assembly and submit live in
 * `useWizardState`. The 6 per-step components live in `./wizard/StepN*`.
 *
 * Behaviour is unchanged — every label, testid, animation, validation rule
 * and side-effect is preserved verbatim. See `/tmp/phase27_backup/` for the
 * pre-refactor file should we ever need to bisect a regression.
 */
import { useEffect } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";

import useWizardState from "./wizard/useWizardState";
import { LAST_STEP, StepIndicator } from "./wizard/_shared";
import Step0DealType from "./wizard/Step0DealType";
import Step1PropertyInfo from "./wizard/Step1PropertyInfo";
import Step2Income from "./wizard/Step2Income";
import Step3Expenses from "./wizard/Step3Expenses";
import Step4Financing from "./wizard/Step4Financing";
import Step5AIAnalysis from "./wizard/Step5AIAnalysis";

export default function DealWizard() {
  const wizard = useWizardState();
  const {
    step, maxReached, jump, back, next, submit,
    isEdit, editId, loading, submitting, dealType, navigate,
  } = wizard;

  // Phase 19 — restore the "last_is_owned" preference on a fresh draft so
  // owner-investors don't have to flip the toggle every time. Lives here so
  // the hook stays free of localStorage assumptions when wrapped in tests.
  useEffect(() => {
    if (isEdit) return;
    try {
      const saved = window.localStorage?.getItem("runidea.wizard.last_is_owned");
      if (saved === "1" && !wizard.form.analysis.is_owned) {
        wizard.setAnalysis("is_owned", true);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  // Phase 17 — same idea for cash purchase default. Idempotent restore.
  useEffect(() => {
    if (isEdit) return;
    try {
      const saved = window.localStorage?.getItem("runidea.wizard.cash_purchase_default");
      if (saved === "1" && !wizard.form.analysis.is_cash_purchase) {
        wizard.setAnalysis("is_cash_purchase", true);
        wizard.setAnalysis("down_payment_percent", 100);
        wizard.setAnalysis("interest_rate", 0);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-24" data-testid="wizard-loading">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
        </div>
      </AppShell>
    );
  }

  const renderStep = () => {
    switch (step) {
      case 1: return <Step0DealType wizard={wizard} />;
      case 2: return <Step1PropertyInfo wizard={wizard} />;
      case 3: return <Step2Income wizard={wizard} />;
      case 4: return <Step3Expenses wizard={wizard} />;
      case 5: return <Step4Financing wizard={wizard} />;
      case 6: return <Step5AIAnalysis wizard={wizard} />;
      default: return null;
    }
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl space-y-8" data-testid="wizard-root">
        <div>
          <button
            type="button"
            onClick={() => navigate(isEdit ? `/deals/${editId}` : "/dashboard")}
            className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
            data-testid="wizard-back-to-dash"
          >
            <ArrowLeft className="h-4 w-4" /> {isEdit ? "Back to deal" : "Back to dashboard"}
          </button>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {isEdit ? "Edit deal" : "New deal analysis"}
          </h1>
        </div>

        <StepIndicator current={step} onJump={jump} maxReached={maxReached} />

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          {renderStep()}
        </div>

        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={back}
            disabled={step === 1}
            data-testid="wizard-back-button"
            className="border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>

          {step < LAST_STEP ? (
            <Button
              type="button"
              onClick={() => {
                // Phase 36 Batch AA.6 — On step 1 the in-card "Continue"
                // and this bottom "Next" are two doors to the same
                // action. Pre-fix only Continue worked because the
                // deal-type card click sets `pendingDealType`, but the
                // bottom button was gated on the CONFIRMED `dealType`.
                // We now treat a pending selection as enough to advance:
                // commit + step++ in one click, exactly like Continue.
                if (
                  step === 1 &&
                  !dealType &&
                  wizard.pendingDealType
                ) {
                  wizard.confirmDealType(wizard.pendingDealType);
                  return;
                }
                next();
              }}
              data-testid="wizard-next-button"
              disabled={step === 1 && !dealType && !wizard.pendingDealType}
              className="bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md disabled:opacity-50 disabled:hover:translate-y-0"
            >
              Next <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="wizard-submit-button"
              className="bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Analyzing…
                </>
              ) : isEdit ? (
                "Save changes"
              ) : (
                "Analyze deal"
              )}
            </Button>
          )}
        </div>
      </div>
    </AppShell>
  );
}
